/**
 * Download Comprehensive Candidate Analysis
 * Collects data from all tabs and generates a downloadable document
 */

import { getDecisionSummaryText } from './candidateAnalysisUtils';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return dateString;
    }
};

const collectSummaryData = (candidate) => {
    const ragAnalysis = typeof candidate.rag_analysis === 'string' 
        ? JSON.parse(candidate.rag_analysis || '{}') 
        : (candidate.rag_analysis || {});
    
    const evalResult = ragAnalysis?.evaluation_result || {};
    
    const summary = getDecisionSummaryText(candidate);
    
    const strengths = [];
    if (evalResult.skills_score?.strengths) {
        strengths.push(...evalResult.skills_score.strengths);
    }
    if (evalResult.experience_score?.strengths) {
        strengths.push(...evalResult.experience_score.strengths);
    }
    if (!strengths.length && candidate.analysis?.overall_assessment?.strengths) {
        strengths.push(...candidate.analysis.overall_assessment.strengths);
    }
    
    const risks = [];
    if (evalResult.skills_score?.gaps) {
        risks.push(...evalResult.skills_score.gaps);
    }
    if (evalResult.experience_score?.gaps) {
        risks.push(...evalResult.experience_score.gaps);
    }
    if (evalResult.ineligibility_reasons) {
        risks.push(...evalResult.ineligibility_reasons);
    }
    if (!risks.length && candidate.analysis?.overall_assessment?.Shortfall_Areas) {
        risks.push(...candidate.analysis.overall_assessment.Shortfall_Areas);
    }
    
    const score = evalResult.calibrated_score || evalResult.fit_score || 
                  candidate.analysis?.fit_score || candidate.analysis?.score || 0;
    const eligibility = evalResult.eligibility_status;
    
    let recommendation = 'Not Available';
    if (eligibility === 'ineligible') {
        recommendation = 'No Hire - Does not meet eligibility requirements';
    } else if (eligibility === 'needs_review') {
        recommendation = 'Hold - Requires additional review';
    } else if (score >= 75) {
        recommendation = 'Hire - Strong candidate with high fit score';
    } else if (score >= 60) {
        recommendation = 'Hold - Moderate fit, consider for further evaluation';
    } else {
        recommendation = 'No Hire - Low fit score';
    }
    
    return {
        hasData: !!(summary || strengths.length || risks.length),
        summary,
        strengths: strengths.slice(0, 5),
        risks: risks.slice(0, 5),
        recommendation,
        score,
        eligibility
    };
};

const collectFitData = (candidate) => {
    let parsedRagAnalysis = candidate.rag_analysis;
    if (typeof candidate.rag_analysis === 'string') {
        try {
            parsedRagAnalysis = JSON.parse(candidate.rag_analysis);
        } catch (e) {
            parsedRagAnalysis = null;
        }
    }
    
    const summaryPerspectives = candidate.analysis?.summary_perspectives 
        || parsedRagAnalysis?.summary_perspectives 
        || candidate.rag_analysis?.summary_perspectives;
    
    if (!summaryPerspectives) {
        return { hasData: false };
    }
    
    return {
        hasData: !!(summaryPerspectives.cto_perspective || 
                   summaryPerspectives.hr_perspective || 
                   summaryPerspectives.ceo_perspective),
        cto: summaryPerspectives.cto_perspective,
        hr: summaryPerspectives.hr_perspective,
        ceo: summaryPerspectives.ceo_perspective
    };
};

const collectCapabilityData = (candidate) => {
    const ragAnalysis = typeof candidate.rag_analysis === 'string' 
        ? JSON.parse(candidate.rag_analysis || '{}') 
        : (candidate.rag_analysis || {});
    
    const evalResult = ragAnalysis?.evaluation_result || {};
    
    const capability = evalResult.capability_score;
    const fit = evalResult.fit_score;
    const calibrated = evalResult.calibrated_score;
    
    const dimensions = [
        { name: 'Skills', score: evalResult.skills_score?.score, weight: evalResult.skills_score?.weight },
        { name: 'Experience', score: evalResult.experience_score?.score, weight: evalResult.experience_score?.weight },
        { name: 'Education', score: evalResult.education_score?.score, weight: evalResult.education_score?.weight },
        { name: 'Projects', score: evalResult.projects_score?.score, weight: evalResult.projects_score?.weight }
    ].filter(d => d.score !== undefined && d.score !== null);
    
    const evidenceQuality = {
        citation_coverage: evalResult.evidence_quality?.citation_coverage,
        contradiction_count: evalResult.evidence_quality?.contradiction_count,
        extraction_completeness: evalResult.evidence_quality?.extraction_completeness,
        requirement_clarity: evalResult.evidence_quality?.requirement_clarity,
        retrieval_strength: evalResult.evidence_quality?.retrieval_strength
    };
    
    const confidence = evalResult.evidence_confidence;
    const percentile = evalResult.calibration_metadata?.benchmark_percentile;
    
    return {
        hasData: !!(capability !== undefined || fit !== undefined || calibrated !== undefined || 
                   dimensions.length || Object.values(evidenceQuality).some(v => v !== undefined) ||
                   confidence !== undefined || percentile !== undefined),
        capability,
        fit,
        calibrated,
        dimensions,
        evidenceQuality,
        confidence,
        percentile
    };
};

const collectHumanInTheLoopData = async (candidate) => {
    try {
        const [decisionRes, historyRes, notesRes, metadataRes] = await Promise.all([
            fetch(`/api/feedback/hiring-decision/${candidate.id}`).catch(() => null),
            fetch(`/api/feedback/score-adjustments/${candidate.id}`).catch(() => null),
            fetch(`/api/feedback/reviewer-notes/${candidate.id}`).catch(() => null),
            fetch(`/api/feedback/reviewer-metadata/${candidate.id}`).catch(() => null)
        ]);
        
        const decision = decisionRes?.ok ? await decisionRes.json() : null;
        const history = historyRes?.ok ? await historyRes.json() : null;
        const notes = notesRes?.ok ? await notesRes.json() : null;
        const metadata = metadataRes?.ok ? await metadataRes.json() : null;
        
        const hasData = !!(decision?.data || history?.adjustments?.length || 
                          notes?.data || metadata?.data);
        
        return {
            hasData,
            decision: decision?.data || null,
            adjustmentHistory: history?.adjustments || [],
            notes: notes?.data || null,
            metadata: metadata?.data || null
        };
    } catch (error) {
        console.error('Error fetching Human in the Loop data:', error);
        return { hasData: false };
    }
};

const collectExplainabilityData = (candidate) => {
    const ragAnalysis = typeof candidate.rag_analysis === 'string' 
        ? JSON.parse(candidate.rag_analysis || '{}') 
        : (candidate.rag_analysis || {});
    
    const evalResult = ragAnalysis?.evaluation_result || {};
    
    const breakdown = {
        overall: evalResult.calibrated_score || evalResult.fit_score || evalResult.overall_score,
        calibrated: evalResult.calibrated_score,
        capability: evalResult.capability_score,
        fit: evalResult.fit_score
    };
    
    const formula = evalResult.score_formula || evalResult.calculation_explanation;
    
    return {
        hasData: !!(breakdown.overall !== undefined || formula),
        breakdown,
        formula
    };
};

const generateHTMLDocument = (candidate, summaryData, fitData, capabilityData, humanData, explainabilityData) => {
    const candidateName = candidate.candidate_name || candidate.filename || 'Unknown Candidate';
    const jobTitle = candidate.job_title || 'Unknown Role';
    
    let html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Candidate Analysis - ${candidateName}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 900px;
            margin: 0 auto;
            padding: 40px 20px;
            background: #fff;
        }
        h1 {
            color: #1a1a2e;
            border-bottom: 3px solid #8b4ad2;
            padding-bottom: 10px;
            margin-bottom: 30px;
        }
        h2 {
            color: #00a6f4;
            margin-top: 40px;
            margin-bottom: 20px;
            border-bottom: 2px solid #e0e0e0;
            padding-bottom: 8px;
        }
        h3 {
            color: #555;
            margin-top: 30px;
            margin-bottom: 15px;
        }
        .section {
            margin-bottom: 30px;
            padding: 20px;
            background: #f9f9f9;
            border-left: 4px solid #8b4ad2;
            border-radius: 4px;
        }
        .score-box {
            display: inline-block;
            padding: 10px 20px;
            background: linear-gradient(135deg, #00a6f4 0%, #8b4ad2 100%);
            color: white;
            border-radius: 8px;
            font-size: 24px;
            font-weight: bold;
            margin: 10px 0;
        }
        .metric-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
            margin: 20px 0;
        }
        .metric-item {
            padding: 15px;
            background: white;
            border-radius: 6px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .metric-label {
            font-size: 12px;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 5px;
        }
        .metric-value {
            font-size: 20px;
            font-weight: bold;
            color: #00a6f4;
        }
        ul {
            list-style-type: none;
            padding-left: 0;
        }
        li {
            padding: 8px 0;
            border-bottom: 1px solid #e0e0e0;
        }
        li:last-child {
            border-bottom: none;
        }
        .strength {
            color: #22c55e;
        }
        .risk {
            color: #ef4444;
        }
        .no-data {
            color: #999;
            font-style: italic;
        }
        .timestamp {
            color: #666;
            font-size: 12px;
            margin-top: 20px;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
        }
        th, td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #e0e0e0;
        }
        th {
            background: #f5f5f5;
            font-weight: 600;
        }
        code {
            background: #f4f4f4;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: 'Courier New', monospace;
        }
    </style>
</head>
<body>
    <h1>Candidate Analysis Report</h1>
    
    <div class="section">
        <h2>Candidate Information</h2>
        <p><strong>Name:</strong> ${candidateName}</p>
        <p><strong>Role:</strong> ${jobTitle}</p>
        <p><strong>Generated:</strong> ${formatDate(new Date().toISOString())}</p>
    </div>`;
    
    // Summary Section
    if (summaryData.hasData) {
        html += `
    <div class="section">
        <h2>Summary</h2>
        ${summaryData.summary ? `<div>${summaryData.summary.replace(/\n/g, '<br>')}</div>` : ''}
        ${summaryData.score !== undefined ? `<div class="score-box">Score: ${summaryData.score.toFixed(1)}</div>` : ''}
        ${summaryData.strengths.length > 0 ? `
        <h3>Key Strengths</h3>
        <ul>
            ${summaryData.strengths.map(s => `<li class="strength">✅ ${s}</li>`).join('')}
        </ul>` : ''}
        ${summaryData.risks.length > 0 ? `
        <h3>Key Risks</h3>
        <ul>
            ${summaryData.risks.map(r => `<li class="risk">⚠️ ${r}</li>`).join('')}
        </ul>` : ''}
        ${summaryData.recommendation ? `<h3>Recommendation</h3><p><strong>${summaryData.recommendation}</strong></p>` : ''}
    </div>`;
    }
    
    // Fit Section
    if (fitData.hasData) {
        html += `
    <div class="section">
        <h2>Fit Assessment</h2>
        ${fitData.cto ? `
        <h3>CTO Perspective</h3>
        <div>${fitData.cto.replace(/\n/g, '<br>')}</div>` : ''}
        ${fitData.hr ? `
        <h3>HR Perspective</h3>
        <div>${fitData.hr.replace(/\n/g, '<br>')}</div>` : ''}
        ${fitData.ceo ? `
        <h3>CEO Perspective</h3>
        <div>${fitData.ceo.replace(/\n/g, '<br>')}</div>` : ''}
    </div>`;
    }
    
    // Capability Section
    if (capabilityData.hasData) {
        html += `
    <div class="section">
        <h2>Capability Assessment</h2>
        <div class="metric-grid">
            ${capabilityData.capability !== undefined ? `
            <div class="metric-item">
                <div class="metric-label">Capability Score</div>
                <div class="metric-value">${capabilityData.capability.toFixed(1)}</div>
            </div>` : ''}
            ${capabilityData.fit !== undefined ? `
            <div class="metric-item">
                <div class="metric-label">Fit Score</div>
                <div class="metric-value">${capabilityData.fit.toFixed(1)}</div>
            </div>` : ''}
            ${capabilityData.calibrated !== undefined ? `
            <div class="metric-item">
                <div class="metric-label">Calibrated Score</div>
                <div class="metric-value">${capabilityData.calibrated.toFixed(1)}</div>
            </div>` : ''}
            ${capabilityData.confidence !== undefined ? `
            <div class="metric-item">
                <div class="metric-label">Confidence</div>
                <div class="metric-value">${(capabilityData.confidence * 100).toFixed(1)}%</div>
            </div>` : ''}
            ${capabilityData.percentile !== undefined ? `
            <div class="metric-item">
                <div class="metric-label">Benchmark Percentile</div>
                <div class="metric-value">${(capabilityData.percentile * 100).toFixed(1)}%</div>
            </div>` : ''}
        </div>`;
        
        if (capabilityData.dimensions.length > 0) {
            html += `
        <h3>Dimension Scores</h3>
        <table>
            <thead>
                <tr>
                    <th>Dimension</th>
                    <th>Score</th>
                    <th>Weight</th>
                </tr>
            </thead>
            <tbody>
                ${capabilityData.dimensions.map(d => `
                <tr>
                    <td>${d.name}</td>
                    <td>${d.score.toFixed(1)}</td>
                    <td>${d.weight ? (d.weight * 100).toFixed(1) + '%' : 'N/A'}</td>
                </tr>`).join('')}
            </tbody>
        </table>`;
        }
        
        const evidenceEntries = Object.entries(capabilityData.evidenceQuality).filter(([_, v]) => v !== undefined);
        if (evidenceEntries.length > 0) {
            html += `
        <h3>Evidence Quality Metrics</h3>
        <table>
            <thead>
                <tr>
                    <th>Metric</th>
                    <th>Value</th>
                </tr>
            </thead>
            <tbody>
                ${evidenceEntries.map(([key, value]) => `
                <tr>
                    <td>${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                    <td>${typeof value === 'number' ? value.toFixed(2) : value}</td>
                </tr>`).join('')}
            </tbody>
        </table>`;
        }
        
        html += `</div>`;
    }
    
    // Human in the Loop Section
    if (humanData.hasData) {
        html += `
    <div class="section">
        <h2>Human in the Loop</h2>`;
        
        if (humanData.decision) {
            html += `
        <h3>Final Decision</h3>
        <p><strong>Decision:</strong> ${humanData.decision.decision || 'N/A'}</p>
        ${humanData.decision.justification ? `<p><strong>Justification:</strong> ${humanData.decision.justification}</p>` : ''}
        ${humanData.decision.is_locked ? `<p><em>Decision has been finalized</em></p>` : ''}`;
        }
        
        if (humanData.adjustmentHistory.length > 0) {
            html += `
        <h3>Score Adjustment History</h3>
        <table>
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Original Score</th>
                    <th>Adjusted Score</th>
                    <th>Reason</th>
                </tr>
            </thead>
            <tbody>
                ${humanData.adjustmentHistory.map(adj => `
                <tr>
                    <td>${formatDate(adj.timestamp)}</td>
                    <td>${adj.original_score?.toFixed(1) || 'N/A'}</td>
                    <td>${adj.adjusted_score?.toFixed(1) || 'N/A'}</td>
                    <td>${adj.reason || 'N/A'}</td>
                </tr>`).join('')}
            </tbody>
        </table>`;
        }
        
        if (humanData.notes) {
            html += `
        <h3>Reviewer Notes</h3>
        ${humanData.notes.strengths ? `<p><strong>Strengths:</strong> ${humanData.notes.strengths}</p>` : ''}
        ${humanData.notes.concerns ? `<p><strong>Concerns:</strong> ${humanData.notes.concerns}</p>` : ''}
        ${humanData.notes.interview_signals ? `<p><strong>Interview Signals:</strong> ${humanData.notes.interview_signals}</p>` : ''}
        ${humanData.notes.team_feedback ? `<p><strong>Team Feedback:</strong> ${humanData.notes.team_feedback}</p>` : ''}`;
        }
        
        if (humanData.metadata) {
            html += `
        <h3>Reviewer Metadata</h3>
        ${humanData.metadata.reviewer_name ? `<p><strong>Reviewer:</strong> ${humanData.metadata.reviewer_name}</p>` : ''}
        ${humanData.metadata.reviewer_role ? `<p><strong>Role:</strong> ${humanData.metadata.reviewer_role}</p>` : ''}
        ${humanData.metadata.decision_confidence ? `<p><strong>Decision Confidence:</strong> ${humanData.metadata.decision_confidence}</p>` : ''}`;
        }
        
        html += `</div>`;
    }
    
    // Explainability Section
    if (explainabilityData.hasData) {
        html += `
    <div class="section">
        <h2>Explainability</h2>`;
        
        if (explainabilityData.breakdown.overall !== undefined) {
            html += `
        <h3>Score Breakdown</h3>
        <div class="metric-grid">
            ${explainabilityData.breakdown.overall !== undefined ? `
            <div class="metric-item">
                <div class="metric-label">Overall Score</div>
                <div class="metric-value">${explainabilityData.breakdown.overall.toFixed(1)}</div>
            </div>` : ''}
            ${explainabilityData.breakdown.calibrated !== undefined ? `
            <div class="metric-item">
                <div class="metric-label">Calibrated Score</div>
                <div class="metric-value">${explainabilityData.breakdown.calibrated.toFixed(1)}</div>
            </div>` : ''}
            ${explainabilityData.breakdown.capability !== undefined ? `
            <div class="metric-item">
                <div class="metric-label">Capability</div>
                <div class="metric-value">${explainabilityData.breakdown.capability.toFixed(1)}</div>
            </div>` : ''}
            ${explainabilityData.breakdown.fit !== undefined ? `
            <div class="metric-item">
                <div class="metric-label">Fit</div>
                <div class="metric-value">${explainabilityData.breakdown.fit.toFixed(1)}</div>
            </div>` : ''}
        </div>`;
        }
        
        if (explainabilityData.formula) {
            html += `
        <h3>Score Formula</h3>
        <div><code>${explainabilityData.formula}</code></div>`;
        }
        
        html += `</div>`;
    }
    
    html += `
    <div class="timestamp">
        Report generated on ${formatDate(new Date().toISOString())}
    </div>
</body>
</html>`;
    
    return html;
};

export const downloadCandidateAnalysis = async (candidate) => {
    try {
        // Collect data from all tabs
        const summaryData = collectSummaryData(candidate);
        const fitData = collectFitData(candidate);
        const capabilityData = collectCapabilityData(candidate);
        const humanData = await collectHumanInTheLoopData(candidate);
        const explainabilityData = collectExplainabilityData(candidate);
        
        // Check if there's any data to download
        if (!summaryData.hasData && !fitData.hasData && !capabilityData.hasData && 
            !humanData.hasData && !explainabilityData.hasData) {
            alert('No analysis data available for this candidate.');
            return;
        }
        
        // Generate HTML document
        const html = generateHTMLDocument(candidate, summaryData, fitData, capabilityData, humanData, explainabilityData);
        
        // Create blob and download
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const candidateName = (candidate.candidate_name || candidate.filename || 'Unknown').replace(/[^a-z0-9]/gi, '_');
        const timestamp = new Date().toISOString().split('T')[0];
        link.download = `Candidate_Analysis_${candidateName}_${timestamp}.html`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
    } catch (error) {
        console.error('Error downloading analysis:', error);
        alert(`Failed to download analysis: ${error.message}. Please try again.`);
    }
};
